import type {
	PortalAccountSnapshotDto,
	PortalCategoryTileDto,
	PortalLastOrderSummaryDto,
	PortalPromoDto,
	PortalStorefrontItemCardDto,
} from '@supply-mover-2-0/shared';
import { portalFetch } from '../lib/apiClient';

/**
 * Storefront homepage dashboard endpoints. The dashboard layout +
 * widgets are admin-authored in the CRM (Settings → Storefront →
 * Dashboard Designs); the storefront just resolves and renders
 * whichever design is assigned to the current portal_site (with
 * tenant-default fallback).
 */

export interface PortalDashboardWidget {
	id: number;
	dashboardId: number;
	reportId?: number | null;
	title: string;
	type?: string;
	position: {
		x: number;
		y: number;
		width: number;
		height: number;
	};
	/**
	 * Per-instance widget config (selection rules, carousel cadence,
	 * grid column count, item limits, etc.) authored in the CRM
	 * dashboard designer. Field is named `settings` on the storefront
	 * endpoint (`/api/portal/dashboard`) — distinct from the CRM-
	 * internal `/api/dashboards/:id/widgets` shape, which carries the
	 * same data under the legacy `widgetConfig` key. Reading the wrong
	 * field here silently swallows every admin-authored setting and
	 * makes the storefront fall back to component defaults regardless
	 * of what was configured.
	 */
	settings?: Record<string, unknown>;
	sortOrder: number;
	isVisible: boolean;
}

export interface PortalDashboardResponse {
	id: number;
	name: string;
	scope: 'CRM' | 'STOREFRONT';
	widgets: Array<PortalDashboardWidget>;
}

/**
 * Resolve the storefront dashboard for the active portal site. Returns
 * null when no design is assigned (storefront falls back to action
 * tiles or straight-to-catalog routing in that case).
 */
export async function getStorefrontDashboard(
	token: string | null,
): Promise<{ success: boolean; data: PortalDashboardResponse | null }> {
	return portalFetch<{
		success: boolean;
		data: PortalDashboardResponse | null;
	}>('/dashboard', token);
}

/**
 * Resolve the storefront ACCOUNT-PAGE design for the active portal site.
 * Login-required. Returns null when no account design is assigned — the
 * account page falls back to its hardcoded card layout in that case.
 */
export async function getStorefrontAccountDashboard(
	token: string | null,
): Promise<{ success: boolean; data: PortalDashboardResponse | null }> {
	return portalFetch<{
		success: boolean;
		data: PortalDashboardResponse | null;
	}>('/dashboard/account-page', token);
}

/**
 * Eligible promos for the active portal site's homepage widgets.
 * Each widget filters / slices this list according to its own
 * selection config.
 */
export async function getStorefrontPromos(
	token: string | null,
): Promise<{ success: boolean; data: Array<PortalPromoDto> }> {
	return portalFetch<{
		success: boolean;
		data: Array<PortalPromoDto>;
	}>('/dashboard/promos', token);
}

export interface PortalPromoDetailResponse {
	promo: PortalPromoDto;
	items: Array<PortalStorefrontItemCardDto>;
	/**
	 * BUY-side category names the promo targets. Always present (may
	 * be empty). When the promo targets categories rather than
	 * specific items, `items` will be a sample drawn from these
	 * categories and the detail page renders a "Applies to" chip row
	 * deep-linking each chip into `/catalog?category=...`. The chip
	 * row is the customer's affordance to actually shop the promo
	 * when there's no curated SKU list to land on.
	 */
	categoryNames: Array<string>;
}

/**
 * Promo detail page data — header (name, hero, terms, CTA) plus the
 * BUY-set items participating in the promo. Login-required upstream
 * (the endpoint is gated PublicPortal); call sites that hit this
 * without a session will get a 401 from the backend, which the
 * caller should redirect to login.
 */
export async function getStorefrontPromoDetail(
	promoId: number,
	token: string | null,
): Promise<{ success: boolean; data: PortalPromoDetailResponse }> {
	return portalFetch<{
		success: boolean;
		data: PortalPromoDetailResponse;
	}>(`/dashboard/promos/${promoId}`, token);
}

/**
 * Account snapshot — open balance, credit limit, available credit,
 * credit-hold flag. Login-required; anonymous visitors get a 401
 * which the calling widget translates into "log in to see your
 * account".
 */
export async function getStorefrontAccountSnapshot(
	token: string | null,
	scope?: string,
): Promise<{ success: boolean; data: PortalAccountSnapshotDto }> {
	// PD-14188: optional roll-up scope (parent / ownership umbrella).
	const qs =
		scope != null && scope !== 'current'
			? `?scope=${encodeURIComponent(scope)}`
			: '';
	return portalFetch<{
		success: boolean;
		data: PortalAccountSnapshotDto;
	}>(`/dashboard/account-snapshot${qs}`, token);
}

/**
 * Most recent order across the visitor's assigned clients. Returns
 * `data: null` when there are no orders yet. Login-required.
 */
export async function getStorefrontLastOrder(
	token: string | null,
): Promise<{ success: boolean; data: PortalLastOrderSummaryDto | null }> {
	return portalFetch<{
		success: boolean;
		data: PortalLastOrderSummaryDto | null;
	}>('/dashboard/last-order', token);
}

/**
 * Reorder candidates for the active customer. Login-required (the
 * underlying query is per-client). Default backend limit is 12.
 */
export async function getStorefrontReorderItems(
	token: string | null,
	limit?: number,
): Promise<{ success: boolean; data: Array<PortalStorefrontItemCardDto> }> {
	const qs = limit != null ? `?limit=${limit}` : '';
	return portalFetch<{
		success: boolean;
		data: Array<PortalStorefrontItemCardDto>;
	}>(`/dashboard/reorder-items${qs}`, token);
}

/**
 * Popular items for the active brand — anonymous-safe. Default
 * backend limit is 12.
 */
export async function getStorefrontPopularItems(
	token: string | null,
	limit?: number,
): Promise<{ success: boolean; data: Array<PortalStorefrontItemCardDto> }> {
	const qs = limit != null ? `?limit=${limit}` : '';
	return portalFetch<{
		success: boolean;
		data: Array<PortalStorefrontItemCardDto>;
	}>(`/dashboard/popular-items${qs}`, token);
}

/**
 * Top-level categories for the homepage Category Tiles widget —
 * anonymous-safe. Default backend limit is 20.
 */
export async function getStorefrontCategoryTiles(
	token: string | null,
	limit?: number,
): Promise<{ success: boolean; data: Array<PortalCategoryTileDto> }> {
	const qs = limit != null ? `?limit=${limit}` : '';
	return portalFetch<{
		success: boolean;
		data: Array<PortalCategoryTileDto>;
	}>(`/dashboard/categories${qs}`, token);
}

/**
 * Items in a single category — feeds the `items-by-category`
 * homepage widget so admins can curate "Top in Sauces" / "Top in
 * Beverages" carousels. Anonymous-safe. Default backend limit 12.
 */
export async function getStorefrontItemsByCategory(
	token: string | null,
	category: string,
	limit?: number,
): Promise<{ success: boolean; data: Array<PortalStorefrontItemCardDto> }> {
	const params = new URLSearchParams({ category });
	if (limit != null) params.set('limit', String(limit));
	return portalFetch<{
		success: boolean;
		data: Array<PortalStorefrontItemCardDto>;
	}>(`/dashboard/items-by-category?${params.toString()}`, token);
}

/**
 * Items participating in a specific promo — feeds the
 * `promo-with-items` homepage widget so admins can pair a promo
 * banner with the actual on-sale items below it. Anonymous-safe.
 */
export async function getStorefrontPromoItems(
	token: string | null,
	promoId: number,
	limit?: number,
): Promise<{ success: boolean; data: Array<PortalStorefrontItemCardDto> }> {
	const qs = limit != null ? `?limit=${limit}` : '';
	return portalFetch<{
		success: boolean;
		data: Array<PortalStorefrontItemCardDto>;
	}>(`/dashboard/promo-items/${promoId}${qs}`, token);
}
